<template>
  <DefaultLayout>
    <div class="networks-page mt-2">
      <div class="row pt-2" style="padding: 0 3rem;">
          <!-- Breadcrumbs -->
          <div class="col-8 p-0">
              <nav aria-label="breadcrumb">
              <ol class="py-1 my-2 breadcrumb align-middle">
                  <li class="breadcrumb-item">
                      <a href="/" class="text-primary fw-bold">
                          {{ $t('homepage') }}
                      </a>
                  </li>
                  <li class="breadcrumb-item">
                      <a class="text-secondary fw-bold">
                          {{ $t('network.networks') }}
                      </a>
                  </li>
              </ol>
              </nav>
          </div>
          <div class="col-4 text-end"></div>
      </div>

      <!-- Navigation Tabs -->
      <div class="row" style="padding: 0 3rem;">
        <div v-if="$auth.auth && hasAnyUserNetworks" class="col-2">
          <span role="button" :class="currentNetworkView == 'yours' ? 'text-primary underlined' : ''" class="tab" @click="changeView('yours')">
            {{ $t('your-networks') }}
            <span v-if="hasSearchTerm && loaded">({{ totalUserNetworkResults }})</span>
          </span>
        </div>
        <div class="col-2 ps-0">
          <span role="button" :class="currentNetworkView == 'discover' ? 'text-primary underlined' : ''" class="tab" @click="changeView('discover')">
            {{ $t('network.discoverNetworks') }}
            <span v-if="hasSearchTerm && loaded">({{ filteredDiscoverNetworks.length }})</span>
          </span>
        </div>
        <div class="col-8"></div><hr style="opacity: 0.5; height: 1px;">
      </div>

      <!-- Search Section -->
      <div class="row" style="padding: 0 2rem 0.7rem;">
        <div class="col-12 px-4">
          <div class="d-flex gap-3 align-items-center">
            <div class="flex-grow-1">
              <div class="input-group">
                <input
                  id="network_search_query"
                  v-model="searchQuery"
                  data-test="network_search_query"
                  type="text"
                  :placeholder="$t('network.search')"
                  class="form-control shadow-sm"
                  @input="handleSearchInput"
                />
                <span class="input-group-text shadow-sm"><i class="fa fa-search"></i></span>
              </div>
            </div>
            <!-- View Toggle Controls for Your Networks -->
            <div v-if="currentNetworkView === 'yours' && (displayManagedNetworks.length > 0 || displayMyNetworks.length > 0)" class="flex-shrink-0">
              <div role="group" aria-label="Network view toggle" class="btn-group">
                <button
                  v-for="(view, index) in network_view"
                  :key="index"
                  role="button"
                  :class="[networksView === view.value ? 'active' : '']"
                  class="btn btn-outline-primary btn-sm"
                  :aria-pressed="networksView === view.value"
                  @click="networksView = view.value"
                >
                  <span class="ms-0" v-html="view.html"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Results Message -->
      <div v-if="hasSearchTerm && loaded" class="row" style="padding: 0 2.6rem;">
        <div class="col-12">
          <div v-if="!hasAnyResults" class="alert alert-info text-center">
            <span class="fs-5">{{ $t('no-results-match-your-search-try-using-less-keywords-or-making-your-search-simpler') }}</span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="!loaded && hasSearchTerm" class="row text-center mt-4">
        <lottie-player
          autoplay
          loop
          speed="2.5"
          src="/img/animations/custom_loader.json"
          style="height: 130px;"
        >
        </lottie-player>
      </div>

      <!-- Network Lists -->
      <div class="row" style="padding: 0 1.5rem;">
        <div v-if="currentNetworkView == 'yours'" class="networks-section">
          <!-- Combined List View for Both Sections -->
          <div v-if="networksView === 'list' && (displayManagedNetworks.length > 0 || displayMyNetworks.length > 0)">
            <NetworkListView
              :networks="combinedNetworksForList"
            />
          </div>

          <!-- Card View - Separate Sections -->
          <div v-if="networksView === 'card'">
            <!-- Managed Networks Section -->
            <div v-if="displayManagedNetworks.length > 0">
              <h5 class="text-secondary ms-3 mb-2">{{ $t('managed-by-you') }}</h5>
              <NetworkList
                id="managed_networks"
                data-test="managed_networks"
                :networks="displayManagedNetworks"
                :hide-footer="true"
              />
            </div>

            <!-- Joined Networks Section -->
            <div v-if="displayMyNetworks.length > 0">
              <h5 class="text-secondary ms-3">{{ $t('joined-by-you') }}</h5>
              <NetworkList
                id="my_networks"
                data-test="my_networks"
                :networks="displayMyNetworks"
                :hide-footer="true"
              />
            </div>
          </div>

          <!-- No Results Message -->
          <div v-if="displayManagedNetworks.length === 0 && displayMyNetworks.length === 0 && !(hasSearchTerm && !hasAnyResults)" class="row" style="padding: 0 0.2rem;">
            <div class="col-12">
              <div class="alert alert-info text-center">
                <span class="fs-5">
                  {{ hasSearchTerm ? $t('no-networks-match-your-search') : $t('no-networks-found') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentNetworkView == 'discover'" class="networks-section">
          <div v-if="isDiscoverNetworksLoading" class="col-12 px-2">
            <div class="alert alert-light py-3 text-center w-100">
              <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                <span class="visually-hidden">{{ $t('network.networkForm.loading') }}</span>
              </div>
              {{ $t('loading-networks') }}
            </div>
          </div>
          <NetworkList v-else-if="displayDiscoverNetworks.length > 0" id="discover_networks" data-test="discover_networks" :networks="displayDiscoverNetworks" />
          <div v-else-if="!(hasSearchTerm && !hasAnyResults)" class="row" style="padding: 0 0.2rem;">
            <div class="col-12">
              <div class="alert alert-info text-center">
                <span class="fs-5">
                  {{ hasSearchTerm ? $t('no-networks-match-your-search') : $t('no-networks-available-to-discover') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>
<script>
import DefaultLayout from './layouts/default/';
import NetworkList from '@/components/commons/Networks/NetworkList.vue';
import NetworkListView from '@/components/commons/Networks/NetworkListView.vue';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  components: {
    DefaultLayout,
    NetworkList,
    NetworkListView,
  },
  data() {
    return {
      searchQuery: '',
      loaded: true,
      filteredManagedNetworks: [],
      filteredMyNetworks: [],
      filteredDiscoverNetworks: [],
      categorizedDiscoverNetworks: [],
      discoverNetworksLoaded: false,
      isUpdatingCategories: false,
      lastNetworkDataHash: null,
      networksView: 'card',
    };
  },
  computed: {
    ...mapState('network', ['myManagedNetworks', 'myNetworks', 'discoverNetworks', 'trendingNetworks', 'userData']),
    hasSearchTerm() {
      return this.searchQuery.trim().length > 0;
    },
    displayManagedNetworks() {
      return this.hasSearchTerm ? this.filteredManagedNetworks : this.myManagedNetworks;
    },
    displayMyNetworks() {
      return this.hasSearchTerm ? this.filteredMyNetworks : this.myNetworks;
    },
    displayDiscoverNetworks() {
      if (this.hasSearchTerm) {
        return this.filteredDiscoverNetworks;
      }
      return this.categorizedDiscoverNetworks;
    },
    isDiscoverNetworksLoading() {
      return (!this.discoverNetworksLoaded || this.isUpdatingCategories) && this.currentNetworkView === 'discover' && !this.hasSearchTerm;
    },
    hasAnyResults() {
      return this.filteredManagedNetworks.length > 0 || 
             this.filteredMyNetworks.length > 0 || 
             this.filteredDiscoverNetworks.length > 0;
    },
    hasAnyUserNetworks() {
      return (this.myManagedNetworks && this.myManagedNetworks.length > 0) || 
             (this.myNetworks && this.myNetworks.length > 0);
    },
    totalUserNetworkResults() {
      return this.filteredManagedNetworks.length + this.filteredMyNetworks.length;
    },
    // computed property to track when network data changes for categorization
    networkDataForCategorization() {
      return this.getNetworkDataHash();
    },
    network_view() {
      return [
        { html: "<i class='fa fa-th'></i> " + this.$t('Main.cards'), value: 'card' },
        { html: "<i class='fa fa-list'></i> " + this.$t('Main.list'), value: 'list' },
      ];
    },
    // combined networks for list view with admin flag
    combinedNetworksForList() {
      const managedWithAdmin = (this.displayManagedNetworks || []).map(network => ({
        ...network,
        is_admin: true
      }));
      const joinedWithAdmin = (this.displayMyNetworks || []).map(network => ({
        ...network,
        is_admin: false
      }));
      return [...managedWithAdmin, ...joinedWithAdmin];
    },
    currentNetworkView() {
      // check if there's a view parameter in the query string
      const viewParam = this.$route.query.view;
      if (viewParam && ['yours', 'discover'].includes(viewParam)) {
        return viewParam;
      }
      // support legacy 'managed'/'joined' parameters by redirecting to 'yours'
      if (viewParam && ['managed', 'joined'].includes(viewParam)) {
        return 'yours';
      }
      // fallback to default behavior (reactively updates when store populates)
      return this.$auth.auth && this.hasAnyUserNetworks ? 'yours' : 'discover';
    },
  },
  watch: {
    '$route.query.view'(newView) {
      // handle legacy route redirects
      if (newView && ['managed', 'joined'].includes(newView)) {
        this.changeView('yours');
      }
    },
    // watch for any change in networks that affects categorization
    networkDataForCategorization: {
      handler(newHash) {
        // only trigger if data actually changed
        if (newHash !== this.lastNetworkDataHash) {
          this.lastNetworkDataHash = newHash;
          this.debouncedCategorizeNetworks();
        }
      }
    }
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.performSearch();
    }, 300);

    this.debouncedCategorizeNetworks = debounce(() => {
      this.updateCategorizedNetworks();
    }, 100);

    // trigger initial categorization after debounced method is created
    this.$nextTick(() => {
      const initialHash = this.getNetworkDataHash();
      if (initialHash && initialHash !== this.lastNetworkDataHash) {
        this.lastNetworkDataHash = initialHash;
        this.debouncedCategorizeNetworks();
      }
    });
  },
  beforeDestroy() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  },
  mounted() {
    this.$store.dispatch('network/loadNetworks', { search: '' });
    if (this.$auth.auth) {
      this.$store.dispatch('network/loadUserData', { search: '' });
    }
  },
  methods: {
    changeView(view) {
      // update URL query parameter
      const same = this.$route.query.view === view;
      this.$router[same ? 'replace' : 'push']({
        name: this.$route.name,
        query: { ...this.$route.query, view }
      });
    },
    handleSearchInput() {
      if (!this.hasSearchTerm) {
        // clear filters immediately when search is empty
        this.filteredManagedNetworks = [];
        this.filteredMyNetworks = [];
        this.filteredDiscoverNetworks = [];
        this.loaded = true;
      } else {
        this.loaded = false;
        this.debouncedSearch();
      }
    },
    performSearch() {
      if (!this.hasSearchTerm) {
        this.loaded = true;
        return;
      }

      const searchTerm = this.searchQuery.trim().toLowerCase();

      // filter networks based on search term
      this.filteredManagedNetworks = this.filterNetworks(this.myManagedNetworks, searchTerm);
      this.filteredMyNetworks = this.filterNetworks(this.myNetworks, searchTerm);
      this.filteredDiscoverNetworks = this.filterNetworks(this.discoverNetworks, searchTerm);
      
      this.loaded = true;
    },
    filterNetworks(networks, searchTerm) {
      if (!networks || networks.length === 0) return [];

      return networks.filter(network => {
        const name = (network.name || '').toLowerCase();
        const purpose = (network.purpose || '').toLowerCase();
        const shortName = (network.short_name || '').toLowerCase();

        return name.includes(searchTerm) || 
               purpose.includes(searchTerm) || 
               shortName.includes(searchTerm);
      });
    },
    scrollTo(id) {
      var element = document.getElementById(id);
      var headerOffset = 80;
      var elementPosition = element.getBoundingClientRect().top;
      var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    },
    categorizeAndOrderDiscoverNetworks() {
      if (!this.discoverNetworks || this.discoverNetworks.length === 0) {
        return [];
      }

      const userNetworks = [...(this.myManagedNetworks || []), ...(this.myNetworks || [])];
      const trendingNetworks = this.trendingNetworks || [];

      // categorize networks using the same logic as Homepage.vue
      const categories = {
        similar: [],
        popular: [],
        active: [],
        trending: [],
        new: [],
        other: []
      };

      this.discoverNetworks.forEach(network => {
        let categorized = false;

        // check for similar networks (if user is logged in)
        if (this.$auth.auth && userNetworks.length > 0) {
          const excludeWords = new Set(['and', 'or', 'the', 'of', 'a', 'an', 'network', 'networks', 'group', 'groups', 'community', 'communities']);

          const networkWords = (network.name || '').toLowerCase()
            .split(/[\s\-_]+/)
            .filter(word => word.length > 3 && !excludeWords.has(word));

          const hasMatch = userNetworks.some(userNetwork => {
            const userWords = (userNetwork.name || '').toLowerCase()
              .split(/[\s\-_]+/)
              .filter(word => word.length > 3 && !excludeWords.has(word));

            return networkWords.some(word => 
              userWords.some(userWord => 
                userWord.includes(word) || word.includes(userWord)
              )
            );
          });

          if (hasMatch) {
            categories.similar.push(network);
            categorized = true;
          }
        }

        // check if it's a trending network
        if (!categorized && trendingNetworks.some(trending => trending.id === network.id)) {
          categories.trending.push(network);
          categorized = true;
        }

        // check member count for popular networks
        if (!categorized && (network.num_users || 0) > 100) {
          categories.popular.push(network);
          categorized = true;
        }

        // check for active networks (if they have recent activity/media)
        if (!categorized && (network.media_count || 0) > 25) {
          categories.active.push(network);
          categorized = true;
        }

        // check if it's a new network (created in the last month)
        if (!categorized && network.created_at) {
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          const createdDate = new Date(network.created_at);
          if (createdDate > oneMonthAgo) {
            categories.new.push(network);
            categorized = true;
          }
        }

        // default category
        if (!categorized) {
          categories.other.push(network);
        }
      });

      // shuffle the order of the first 3 priority categories
      const priorityCategories = ['similar', 'popular', 'active'];
      const shuffledPriorities = this.shuffleArray([...priorityCategories]);

      let orderedNetworks = [];

      // add up to 10 networks from each of the first 3 shuffled priority categories
      shuffledPriorities.forEach(categoryName => {
        const categoryNetworks = this.shuffleArray([...categories[categoryName]]);
        orderedNetworks = [...orderedNetworks, ...categoryNetworks.slice(0, 10)];
      });

      // add trending networks (shuffled)
      const shuffledTrending = this.shuffleArray([...categories.trending]);
      orderedNetworks = [...orderedNetworks, ...shuffledTrending];

      // add new networks (shuffled)
      const shuffledNew = this.shuffleArray([...categories.new]);
      orderedNetworks = [...orderedNetworks, ...shuffledNew];

      // add remaining networks (shuffled)
      const shuffledOther = this.shuffleArray([...categories.other]);
      orderedNetworks = [...orderedNetworks, ...shuffledOther];

      return orderedNetworks;
    },
    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    getNetworkDataHash() {
      // create a simple hash based on network counts and key properties
      const discoverCount = this.discoverNetworks?.length || 0;
      const myNetworksCount = this.myNetworks?.length || 0;
      const managedCount = this.myManagedNetworks?.length || 0;
      const trendingCount = this.trendingNetworks?.length || 0;

      // include first few network IDs to detect actual content changes
      const discoverIds = (this.discoverNetworks || []).slice(0, 5).map(n => n.id).join(',');
      const myNetworkIds = (this.myNetworks || []).slice(0, 3).map(n => n.id).join(',');
      const managedIds = (this.myManagedNetworks || []).slice(0, 3).map(n => n.id).join(',');

      return `${discoverCount}-${myNetworksCount}-${managedCount}-${trendingCount}-${discoverIds}-${myNetworkIds}-${managedIds}`;
    },
    updateCategorizedNetworks() {
      if (this.isUpdatingCategories) {
        return; // prevent concurrent updates
      }

      if (!this.discoverNetworks || this.discoverNetworks.length === 0) {
        this.discoverNetworksLoaded = true;
        this.isUpdatingCategories = false;
        this.categorizedDiscoverNetworks = [];
        return;
      }

      this.isUpdatingCategories = true;

      // use nextTick to ensure DOM updates happen smoothly
      this.$nextTick(() => {
        this.categorizedDiscoverNetworks = this.categorizeAndOrderDiscoverNetworks();
        this.discoverNetworksLoaded = true;
        this.isUpdatingCategories = false;
      });
    },
  },
};
</script>
<style scoped>
.networks-page .nav-pills .nav-item .nav-link {
  border-radius: 10px !important;
  margin-right: 10px;
  border: none;
}
.networks-page .nav-pills .nav-item .nav-link.active {
  background: none;
  font-weight: bolder;
  color: #926dde;
}
.tab {
  font-weight: 600;
  font-size: 20px;
}
.networks-section i {
  font-size: 25px;
}
.underlined {
  text-decoration-line: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 9px;
}
.tab:hover {
  color: #005f9a;
}
.vs__dropdown-toggle {
  border-color: #ccc !important;
}
.vs__selected-options {
  padding: 0px 0px 3px 2px;
}
.networks-section .reportrange-text {
  height: 45px;
}
/* AG Grid styling */
.ag-theme-alpine {
  --ag-header-height: 40px;
  --ag-header-foreground-color: #5C6D80;
  --ag-header-background-color: #f8f9fa;
  --ag-header-cell-hover-background-color: #e9ecef;
  --ag-header-cell-moving-background-color: #e9ecef;
  --ag-row-hover-color: #e9ecef;
  --ag-selected-row-background-color: rgba(0, 97, 182, 0.1);
  --ag-row-height: 40px;
  --ag-font-size: 14px;
  --ag-font-family: 'Nunito', sans-serif;
}
.ag-theme-alpine .ag-header-cell-label {
  justify-content: center;
}
.ag-theme-alpine .ag-floating-filter-input {
  font-size: 14px;
}
@media (min-width: 2000px){
  .custom-margin {
    margin-right: -30px !important;
  }
}
</style>